import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@mui/material';

import { Chip } from '../shared/Chip';
import { Icon } from '../shared/Icon';
import Pagination from '../shared/pagination/Pagination';
import { ProtectedButton } from '../shared/ProtectedButton';
import { TableColumn } from '../shared/Table/TableColumn';
import { createSortFn, SortDescriptor } from '../shared/Table/TableUtils';
import { TEMPLATE_DISPLAY_NAMES } from './model/PostTemplates';
import { POST_STATE_COLOR_MAP, POST_STATE_DISPLAY_MAP } from './Post';
import { PostThumbnail } from './PostThumbnail';
import { LabelInput, Modal } from '../shared';
import { PortalPostState, POST_STATES, UserRoles } from '@lifeserver/shared';

import {
    Post,
    useDuplicatePortalPost,
    usePortalApi,
    usePortalPosts,
} from '../hooks/portal';
import { useSearchParamState } from '../hooks/useSearchParamState';

import { formatDate } from '../utils/dates';
import { handleEnvAuth } from '../utils/envaccess';
import { errorLikeToMessage } from '../utils/error';

import { RouteTo } from '../routes';

import styles from './PortalPostLibrary.module.scss';
import { highlightMatch } from './portalUiUtils';
import {
    copyPortalPostToEnv,
    getPublicationStatus,
    promptAndDuplicatePost,
} from './portalUtils';

const PAGE_SIZE = 10;

const DuplicateIcon = () => (
    <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="14"
        height="14"
    >
        <path
            fill="currentColor"
            d="M6.569 2.25h6.362c.604 0 1.102 0 1.508.033c.421.035.809.109 1.173.294a3 3 0 0 1 1.311 1.311c.186.364.26.752.294 1.173c.033.406.033.904.033 1.508v.181h.4c.503 0 .918 0 1.257.023c.352.024.678.076.991.205a3 3 0 0 1 1.624 1.624c.13.313.18.639.205.99c.023.34.023.755.023 1.257v.052c0 .502 0 .917-.023 1.256a3.2 3.2 0 0 1-.189.95a12.43 12.43 0 0 1-8.236 8.37l-.189.059a3.1 3.1 0 0 1-.956.19c-.34.024-.754.024-1.256.024h-.052c-.502 0-.917 0-1.256-.023c-.352-.024-.678-.076-.991-.205a3 3 0 0 1-1.624-1.624c-.13-.313-.18-.639-.205-.99c-.023-.34-.023-.755-.023-1.257v-.401h-.18c-.604 0-1.103 0-1.509-.033c-.421-.035-.809-.108-1.173-.294a3 3 0 0 1-1.311-1.311c-.185-.364-.26-.752-.294-1.173c-.033-.406-.033-.904-.033-1.508V6.569c0-.604 0-1.102.033-1.508c.035-.421.109-.809.294-1.173a3 3 0 0 1 1.311-1.311c.364-.185.752-.26 1.173-.294c.406-.033.904-.033 1.508-.033M8.25 17.625c0 .534 0 .898.02 1.18c.018.276.053.419.094.519a1.5 1.5 0 0 0 .812.812c.1.041.243.076.519.094c.282.02.646.02 1.18.02s.898 0 1.18-.02c.276-.018.419-.053.519-.094a1.5 1.5 0 0 0 .812-.812c.041-.1.075-.243.094-.519c.02-.282.02-.646.02-1.18v-.026c0-.502 0-.917.023-1.256c.024-.352.076-.678.205-.991a3 3 0 0 1 1.624-1.624c.313-.13.639-.18.99-.205c.34-.023.755-.023 1.257-.023h.026c.534 0 .898 0 1.18-.02c.276-.019.419-.053.519-.094a1.5 1.5 0 0 0 .812-.812c.041-.1.076-.243.094-.519c.02-.282.02-.646.02-1.18s0-.898-.02-1.18c-.018-.276-.053-.419-.094-.519a1.5 1.5 0 0 0-.812-.812c-.1-.041-.243-.076-.519-.094c-.282-.02-.646-.02-1.18-.02H11.1c-.642 0-1.08 0-1.417.028c-.329.027-.497.076-.614.135a1.5 1.5 0 0 0-.656.656c-.06.117-.108.285-.135.614c-.027.338-.028.775-.028 1.417z"
        />
    </svg>
);

export type PostStatus = 'published' | 'scheduled' | 'excluded';

export const POST_STATUS_COLOR_MAP: Record<PostStatus, string> = {
    scheduled: '#EAF3F2',
    published: '#20882E',
    excluded: '#DC2626',
};
export const POST_STATUS_DISPLAY_MAP: Record<PostStatus, string> = {
    published: 'Published',
    scheduled: 'Scheduled',
    excluded: 'Unable to publish',
};

export const PortalPostLibrary = () => {
    const navigate = useNavigate();
    const { data: posts, deletePost } = usePortalPosts();
    const duplicatePortalPostMutation = useDuplicatePortalPost();
    const portalApi = usePortalApi();

    const tableData = useMemo(() => {
        return (posts ?? []).map((post) => {
            const publicationStatuses = post.publications.map((publication) =>
                getPublicationStatus(publication),
            );

            const status = publicationStatuses.reduce<PostStatus | undefined>(
                (prev, status) => {
                    // If multiple publications, prefer published > scheduled
                    const tuple = [prev, status];
                    if (tuple.includes('published')) {
                        // Check if post state is ready, otherwise excluded
                        return post.state === 'ready'
                            ? 'published'
                            : 'excluded';
                    }
                    if (tuple.includes('scheduled')) {
                        return 'scheduled';
                    }
                    return undefined;
                },
                undefined,
            );

            // Generate tooltip based on status
            let statusTooltip: string | undefined;
            if (status === 'scheduled') {
                const nextScheduled = post.publications
                    .filter((pub) => getPublicationStatus(pub) === 'scheduled')
                    .sort(
                        (a, b) =>
                            new Date(a.startTime).getTime() -
                            new Date(b.startTime).getTime(),
                    )[0];
                if (nextScheduled) {
                    statusTooltip = new Date(
                        nextScheduled.startTime,
                    ).toLocaleString();
                }
            } else if (status === 'published') {
                statusTooltip = 'Post is in the current published feed';
            } else if (status === 'excluded') {
                statusTooltip = 'Should be published but post is not ready';
            }

            return {
                thumbnail: (
                    <PostThumbnail
                        postData={post.payload}
                        templateId={post.templateId}
                        height={48}
                        className={styles.thumbnail}
                    />
                ),
                id: post.id,
                name: post.id,
                type: post.templateId,
                isDeletable: !post.publications.length,
                createdAt: post.createdAt,
                createdBy: post.createdBy,
                modifiedAt: post.modifiedAt,
                modifiedBy: post.modifiedBy,
                tags: post.tags?.map((tag) => tag.name) ?? [],
                tagsString: post.tags?.map((tag) => tag.name).join(', ') ?? '',
                state: post.state,
                status,
                statusTooltip,
            };
        });
    }, [posts]);

    const [sortDescriptor, setSortDescriptor] =
        useSearchParamState<SortDescriptor<(typeof tableData)[number]> | null>(
            {
                column: 'modifiedAt',
                direction: 'desc',
            },
            'sort',
        );
    const [searchValue, setSearchValue] = useSearchParamState('', 'search');
    const [templateFilter, setTemplateFilter] = useSearchParamState(
        'all',
        'template',
    );
    const [stateFilter, setStateFilter] = useSearchParamState('all', 'state');
    const [feedFilter, setFeedFilter] = useSearchParamState('all', 'feed');
    const [page] = useSearchParamState(1, 'page');

    const onSearchUpdate = (value: string) => {
        setSearchValue(value, { page: 1 });
    };

    const onTemplateFilterChange = (value: string) => {
        setTemplateFilter(value, { page: 1 });
    };

    const onStateFilterChange = (value: string) => {
        setStateFilter(value, { page: 1 });
    };

    const onFeedFilterChange = (value: string) => {
        setFeedFilter(value, { page: 1 });
    };

    const templateOptions = useMemo(() => {
        const uniqueTemplates = new Set(tableData.map((post) => post.type));
        return ['all', ...Array.from(uniqueTemplates)];
    }, [tableData]);

    const feedOptions = useMemo(() => {
        return ['all', 'published', 'scheduled', 'excluded'];
    }, []);

    const stateOptions = useMemo(() => {
        return ['all', ...POST_STATES];
    }, []);

    const filteredData = useMemo(() => {
        let filteredData = tableData;
        const normalizedSearchValue = searchValue.toLowerCase();
        if (searchValue) {
            filteredData = filteredData?.filter(
                (post) =>
                    post.type.toLowerCase().includes(normalizedSearchValue) ||
                    post.name.toLowerCase().includes(normalizedSearchValue) ||
                    post.tagsString
                        ?.toLowerCase()
                        .includes(normalizedSearchValue),
            );
        }
        if (templateFilter && templateFilter !== 'all') {
            filteredData = filteredData?.filter(
                (post) => post.type === templateFilter,
            );
        }
        if (stateFilter && stateFilter !== 'all') {
            filteredData = filteredData?.filter(
                (post) => post.state === stateFilter,
            );
        }
        if (feedFilter && feedFilter !== 'all') {
            filteredData = filteredData?.filter(
                (post) => post.status === feedFilter,
            );
        }
        if (sortDescriptor) {
            filteredData = filteredData?.toSorted(createSortFn(sortDescriptor));
        }

        return filteredData;
    }, [
        tableData,
        searchValue,
        templateFilter,
        feedFilter,
        stateFilter,
        sortDescriptor,
    ]);

    const paginatedData = useMemo(() => {
        return filteredData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    }, [filteredData, page]);

    const onClickDelete = async (postId: Post['id']) => {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete post',
            text: 'After the post is deleted, it cannot be restored. Are you sure you want to delete?',
            okLabel: 'Delete',
        });
        if (!isConfirmed) {
            return;
        }
        deletePost.mutate({ postId });
    };

    const onClickDuplicate = async (post: (typeof tableData)[number]) => {
        await promptAndDuplicatePost(
            post.id,
            (newName) =>
                duplicatePortalPostMutation.mutateAsync({
                    postId: post.id,
                    newName,
                }),
            (newPost) => navigate(RouteTo.PortalPostEdit(newPost.id)),
        );
    };

    const onClickCopyToEnv = async (post: (typeof tableData)[number]) => {
        const sourcePost = posts?.find((p) => p.id === post.id);
        if (!sourcePost) return;

        const targetEnv = await handleEnvAuth();
        if (!targetEnv) return;

        const isConfirmed = await Modal.confirm({
            heading: 'Copy post to environment',
            text: `Copy post "${post.id}" to ${targetEnv.name}? If a post with the same id already exists there, it will be overwritten.`,
            okLabel: 'Copy',
        });
        if (!isConfirmed) return;

        try {
            await copyPortalPostToEnv(portalApi, sourcePost, targetEnv);
            await Modal.alert({
                heading: 'Copying done!',
                text: `Post "${post.id}" was copied to ${targetEnv.name}.`,
            });
        } catch (e) {
            await Modal.alert({
                heading: 'Oh no!',
                text: `Failed to copy post: ${errorLikeToMessage(e)}`,
            });
        }
    };

    return (
        <>
            <menu className={styles.menu}>
                <LabelInput
                    value={searchValue}
                    label="Search"
                    size="normal"
                    onChange={(e) => onSearchUpdate(e.target.value)}
                />
                <div className={styles.templateFilter}>
                    <label className={styles.label} htmlFor="templateFilter">
                        TEMPLATES
                    </label>
                    <select
                        id="templateFilter"
                        value={templateFilter}
                        onChange={(e) => onTemplateFilterChange(e.target.value)}
                    >
                        <option value="all">All</option>
                        {templateOptions
                            .filter((template) => template !== 'all')
                            .map((template) => (
                                <option key={template} value={template}>
                                    {
                                        TEMPLATE_DISPLAY_NAMES[
                                            template as keyof typeof TEMPLATE_DISPLAY_NAMES
                                        ]
                                    }
                                </option>
                            ))}
                    </select>
                </div>
                <div className={styles.feedFilter}>
                    <label className={styles.label} htmlFor="feedFilter">
                        FEED
                    </label>
                    <select
                        id="feedFilter"
                        value={feedFilter}
                        onChange={(e) => onFeedFilterChange(e.target.value)}
                    >
                        <option value="all">All</option>
                        {feedOptions
                            .filter((status) => status !== 'all')
                            .map((status) => (
                                <option key={status} value={status}>
                                    {
                                        POST_STATUS_DISPLAY_MAP[
                                            status as PostStatus
                                        ]
                                    }
                                </option>
                            ))}
                    </select>
                </div>
                <div className={styles.stateFilter}>
                    <label className={styles.label} htmlFor="stateFilter">
                        STATE
                    </label>
                    <select
                        id="stateFilter"
                        value={stateFilter}
                        onChange={(e) => onStateFilterChange(e.target.value)}
                    >
                        <option value="all">All</option>
                        {stateOptions
                            .filter((status) => status !== 'all')
                            .map((status) => (
                                <option key={status} value={status}>
                                    {
                                        POST_STATE_DISPLAY_MAP[
                                            status as PortalPostState
                                        ]
                                    }
                                </option>
                            ))}
                    </select>
                </div>
                <div className={styles.createButton}>
                    <ProtectedButton
                        role={UserRoles.PortalWritePost}
                        variant="contained"
                        onClick={() => navigate(RouteTo.PortalPostNew())}
                        startIcon={<Icon name="add" />}
                    >
                        Create post
                    </ProtectedButton>
                </div>
            </menu>

            {posts && filteredData && posts.length !== filteredData.length && (
                <p>
                    (Showing {filteredData.length} out of {posts.length} posts)
                </p>
            )}
            <table className={styles.table}>
                <thead>
                    <tr>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="state"
                        >
                            State
                        </TableColumn>
                        <TableColumn>{/* Thumbnail*/}</TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="name"
                        >
                            Name
                        </TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="modifiedAt"
                        >
                            Modified
                        </TableColumn>
                        <TableColumn>Tags</TableColumn>
                        <TableColumn
                            allowSorting
                            activeSortDescriptor={sortDescriptor}
                            onSortChange={setSortDescriptor}
                            column="status"
                        >
                            Feed
                        </TableColumn>
                        <th>{/* Actions */}</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData?.map((post) => {
                        return (
                            <tr
                                key={post.id}
                                onClick={() =>
                                    navigate(RouteTo.PortalPostEdit(post.id))
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        navigate(
                                            RouteTo.PortalPostEdit(post.id),
                                        );
                                    }
                                }}
                            >
                                <td>
                                    <Chip
                                        customColor={
                                            POST_STATE_COLOR_MAP[post.state]
                                        }
                                        customTextColor={
                                            post.state === 'todo'
                                                ? '#000000'
                                                : '#FFFFFF'
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onStateFilterChange(post.state);
                                        }}
                                    >
                                        {POST_STATE_DISPLAY_MAP[post.state]}
                                    </Chip>
                                </td>
                                <td className={styles.thumbnailColumn}>
                                    {post.thumbnail}
                                </td>
                                <td>
                                    {highlightMatch(
                                        post.name,
                                        searchValue,
                                        'highlight',
                                    )}
                                </td>
                                <td>
                                    <p className={styles.timestamp}>
                                        {formatDate(post.modifiedAt)}
                                    </p>
                                    <p className={styles.author}>
                                        {post.modifiedBy ?? 'Unknown'}
                                    </p>
                                </td>
                                <td className={styles.tagsColumn}>
                                    {post.tags && post.tags.length > 0 && (
                                        <div className={styles.tagsContainer}>
                                            {post.tags
                                                .slice(0, 3)
                                                .map((tag, index) => (
                                                    <Chip
                                                        key={index}
                                                        color="secondary"
                                                    >
                                                        {tag}
                                                    </Chip>
                                                ))}
                                            {post.tags.length > 3 && (
                                                <Chip color="secondary">
                                                    +{post.tags.length - 3}
                                                </Chip>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {post.status && (
                                        <Chip
                                            customColor={
                                                POST_STATUS_COLOR_MAP[
                                                    post.status
                                                ]
                                            }
                                            startIcon={
                                                post.status === 'scheduled' ? (
                                                    <Icon name="calendar_clock" />
                                                ) : post.status ===
                                                  'excluded' ? (
                                                    <Icon name="event_busy" />
                                                ) : undefined
                                            }
                                            customTextColor={
                                                post.status === 'scheduled'
                                                    ? '#000000'
                                                    : '#FFFFFF'
                                            }
                                            tooltip={post.statusTooltip}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (post.status) {
                                                    onFeedFilterChange(
                                                        post.status,
                                                    );
                                                }
                                            }}
                                        >
                                            {
                                                POST_STATUS_DISPLAY_MAP[
                                                    post.status
                                                ]
                                            }
                                        </Chip>
                                    )}
                                </td>

                                <td className={styles.actionsContainer}>
                                    {post.isDeletable && (
                                        <ProtectedButton
                                            role={UserRoles.PortalWritePost}
                                            startIcon={<Icon name="delete" />}
                                            disabled={!post.isDeletable}
                                            color="error"
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onClickDelete(post.id);
                                            }}
                                        >
                                            Delete
                                        </ProtectedButton>
                                    )}
                                    <ProtectedButton
                                        role={UserRoles.PortalWritePost}
                                        startIcon={<DuplicateIcon />}
                                        variant="outlined"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickDuplicate(post);
                                        }}
                                    >
                                        Duplicate
                                    </ProtectedButton>
                                    <ProtectedButton
                                        role={UserRoles.PortalWritePost}
                                        startIcon={<Icon name="upload" />}
                                        variant="outlined"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickCopyToEnv(post);
                                        }}
                                    >
                                        Copy to env
                                    </ProtectedButton>
                                    <Button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(
                                                RouteTo.PortalPostEdit(post.id),
                                            );
                                        }}
                                        color="primary"
                                        variant="contained"
                                    >
                                        Edit
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {filteredData && filteredData.length > PAGE_SIZE && (
                <Pagination
                    currentPage={page}
                    totalPageCount={Math.ceil(
                        (filteredData?.length ?? 0) / PAGE_SIZE,
                    )}
                />
            )}
        </>
    );
};
